import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { createTopic, deleteTopic, listTopics, patchTopic } from '../../services/whatsappApi';
import type { AssistantTopic, NewTopicInput } from '../../types/whatsapp';
import { Switch } from './Switch';
import { Toast } from './Toast';
import { TopicForm, emptyTopic } from './TopicForm';

const cardClass = 'rounded-[20px] border border-stone-200 bg-white p-6';

const errorText = (err: unknown, fallback: string): string => (err instanceof Error && err.message ? err.message : fallback);

const WarningIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 9v4" />
    <path d="M12 17h.01" />
    <path d="M10.3 3.9 2.4 18a2 2 0 0 0 1.7 3h15.8a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
  </svg>
);

const SparkIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8L12 3Z" />
  </svg>
);

const PersonIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="8" r="4" />
    <path d="M4 21c0-4 3.6-7 8-7s8 3 8 7" />
  </svg>
);

const ALWAYS_TEAM = [
  { title: 'Síntomas y diagnósticos', text: 'El asistente no da consejo médico.' },
  { title: 'Urgencias', text: 'Dolor, pérdida de visión, golpes.' },
  { title: 'Temas ajenos a la clínica', text: 'El asistente indica que no puede ayudar con eso.' },
];

interface ToastState {
  message: ReactNode;
  undo?: () => void;
}

export const AssistantTab = () => {
  const [topics, setTopics] = useState<AssistantTopic[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState<ReadonlySet<string>>(new Set());
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastState | null>(null);

  const topicsRef = useRef(topics);
  topicsRef.current = topics;

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      setTopics(await listTopics());
    } catch (err) {
      setLoadError(errorText(err, 'No se pudieron cargar los temas.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const replaceTopic = (updated: AssistantTopic) =>
    setTopics((prev) => prev.map((t) => (t.key === updated.key ? updated : t)));

  const setBusyKey = (key: string, on: boolean) =>
    setBusy((prev) => {
      const next = new Set(prev);
      if (on) next.add(key);
      else next.delete(key);
      return next;
    });

  const setRowError = (key: string, message: string | null) =>
    setRowErrors((prev) => {
      const next = { ...prev };
      if (message) next[key] = message;
      else delete next[key];
      return next;
    });

  const setEnabled = useCallback(async (key: string, enabled: boolean, announce: boolean) => {
    const before = topicsRef.current.find((t) => t.key === key);
    if (!before) return;
    setRowError(key, null);
    setBusyKey(key, true);
    setTopics((prev) => prev.map((t) => (t.key === key ? { ...t, enabled } : t)));
    try {
      const updated = await patchTopic(key, { enabled });
      replaceTopic(updated);
      if (announce) {
        setToast({
          message: (
            <>
              {enabled ? 'Se activó' : 'Se apagó'} <span className="font-semibold">{updated.label}</span>
            </>
          ),
          undo: () => {
            setToast(null);
            void setEnabled(key, !enabled, false);
          },
        });
      }
    } catch (err) {
      replaceTopic(before);
      setRowError(key, errorText(err, 'No se pudo cambiar el tema.'));
    } finally {
      setBusyKey(key, false);
    }
  }, []);

  const onToggle = (t: AssistantTopic) => {
    if (t.missing_content && !t.enabled) {
      setCreating(false);
      setOpenKey(t.key);
      return;
    }
    void setEnabled(t.key, !t.enabled, true);
  };

  const openRow = (key: string | null) => {
    setCreating(false);
    setFormError(null);
    setOpenKey(key);
  };

  const saveTopic = async (t: AssistantTopic, values: NewTopicInput) => {
    setSaving(true);
    setFormError(null);
    try {
      const patch = t.is_system
        ? { content: values.content }
        : { label: values.label, description: values.description, examples: values.examples, content: values.content };
      replaceTopic(await patchTopic(t.key, patch));
      setOpenKey(null);
    } catch (err) {
      setFormError(errorText(err, 'No se pudo guardar el tema.'));
    } finally {
      setSaving(false);
    }
  };

  const removeTopic = async (t: AssistantTopic) => {
    setSaving(true);
    setFormError(null);
    try {
      await deleteTopic(t.key);
      setTopics((prev) => prev.filter((x) => x.key !== t.key));
      setOpenKey(null);
    } catch (err) {
      setFormError(errorText(err, 'No se pudo eliminar el tema.'));
    } finally {
      setSaving(false);
    }
  };

  const addTopic = async (values: NewTopicInput) => {
    setSaving(true);
    setFormError(null);
    try {
      const created = await createTopic(values);
      setTopics((prev) => [...prev, created]);
      setCreating(false);
      setToast({ message: <>Se creó <span className="font-semibold">{created.label}</span></> });
    } catch (err) {
      setFormError(errorText(err, 'No se pudo crear el tema.'));
    } finally {
      setSaving(false);
    }
  };

  const missing = topics.filter((t) => t.missing_content).length;
  const dismissToast = useCallback(() => setToast(null), []);

  return (
    <div className="flex flex-col gap-5">
      <section className={`${cardClass} flex flex-col gap-4`}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="max-w-[560px]">
            <h2 className="m-0 text-xl font-bold text-stone-900">Temas que el asistente puede atender</h2>
            <p className="mt-1 text-sm leading-6 text-stone-600">
              Si un tema está apagado, el asistente no responde sobre eso y pasa la conversación al equipo.
            </p>
            {missing > 0 && (
              <p className="mt-2 flex items-center gap-1.5 text-sm font-semibold text-amber-800">
                <WarningIcon />
                {missing === 1 ? '1 tema necesita contenido' : `${missing} temas necesitan contenido`} antes de poder activarse.
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={() => {
              setOpenKey(null);
              setFormError(null);
              setCreating((c) => !c);
            }}
            className="flex h-10 items-center gap-1.5 rounded-full border border-primary bg-white px-[18px] text-sm font-semibold text-primary hover:bg-blue-50"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
              <path d="M12 5v14M5 12h14" />
            </svg>
            Agregar tema
          </button>
        </div>

        {creating && (
          <section aria-labelledby="nuevo-tema" className="flex flex-col gap-4 rounded-2xl border border-blue-300 bg-white p-5">
            <h3 id="nuevo-tema" className="m-0 text-base font-bold text-stone-900">Nuevo tema</h3>
            <TopicForm
              fullForm
              initial={emptyTopic}
              idPrefix="nuevo"
              saveLabel="Guardar tema"
              saving={saving}
              error={formError}
              footerNote="El tema se activa al guardarlo, si tiene contenido."
              onSave={(v) => void addTopic(v)}
              onCancel={() => setCreating(false)}
            />
          </section>
        )}

        {loading && <div className="py-8 text-center text-stone-400">Cargando…</div>}

        {loadError && (
          <div role="alert" className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <span>{loadError}</span>
            <button type="button" onClick={() => void load()} className="h-9 rounded-full border border-red-200 bg-white px-4 text-sm font-semibold text-red-700">
              Reintentar
            </button>
          </div>
        )}

        {!loading && !loadError && (
          <ul className="m-0 flex list-none flex-col gap-2.5 p-0">
            {topics.map((t) => {
              const open = openKey === t.key;
              return (
                <li
                  key={t.key}
                  className={`flex flex-col gap-3.5 rounded-2xl border p-4 ${open ? 'border-blue-300' : 'border-stone-200'} ${t.enabled ? 'bg-white' : 'bg-stone-50'}`}
                >
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
                    <div className="min-w-[220px] flex-1">
                      <div className="text-[15px] font-semibold text-stone-900">{t.label}</div>
                      <div className="mt-0.5 line-clamp-2 text-[13px] leading-[1.45] text-stone-600">{t.description}</div>
                      {t.missing_content && (
                        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] font-semibold text-amber-800">
                          <span className="flex items-center gap-1.5"><WarningIcon />Falta contenido</span>
                          <button type="button" onClick={() => openRow(open ? null : t.key)} className="border-0 bg-transparent p-0 font-semibold text-primary">
                            Completar
                          </button>
                        </div>
                      )}
                    </div>
                    {!t.missing_content && (
                      <button type="button" onClick={() => openRow(open ? null : t.key)} aria-expanded={open} className="border-0 bg-transparent p-0 text-[13px] font-semibold text-primary">
                        {open ? 'Cerrar' : 'Editar'}
                      </button>
                    )}
                    {t.enabled ? (
                      <span className="flex h-[38px] flex-none items-center gap-1.5 rounded-full bg-blue-50 px-3.5 text-[13px] font-medium text-blue-700"><SparkIcon />Responde el asistente</span>
                    ) : (
                      <span className="flex h-[38px] flex-none items-center gap-1.5 rounded-full bg-stone-100 px-3.5 text-[13px] font-medium text-stone-600"><PersonIcon />Pasar al equipo</span>
                    )}
                    <Switch
                      checked={t.enabled}
                      disabled={t.missing_content && !t.enabled}
                      busy={busy.has(t.key)}
                      label={`${t.label}: ${t.enabled ? 'activo' : 'apagado'}`}
                      onChange={() => onToggle(t)}
                    />
                  </div>

                  {rowErrors[t.key] && (
                    <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-700">{rowErrors[t.key]}</div>
                  )}

                  {open && (
                    <div className="border-t border-stone-200 pt-3.5">
                      <TopicForm
                        key={t.key}
                        fullForm={!t.is_system}
                        initial={{ label: t.label, description: t.description, examples: t.examples, content: t.content }}
                        idPrefix={`tema-${t.key}`}
                        saveLabel="Guardar tema"
                        saving={saving}
                        error={formError}
                        onSave={(v) => void saveTopic(t, v)}
                        onCancel={() => openRow(null)}
                        onDelete={t.is_system ? undefined : () => void removeTopic(t)}
                      />
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section className="flex flex-col gap-3.5 rounded-[20px] border border-stone-200 bg-stone-100 p-6">
        <div className="flex items-center gap-2.5">
          <div className="flex h-8 w-8 items-center justify-center rounded-[10px] border border-stone-200 bg-white text-stone-600">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="4" y="11" width="16" height="10" rx="2" />
              <path d="M8 11V7a4 4 0 0 1 8 0v4" />
            </svg>
          </div>
          <h2 className="m-0 text-base font-bold text-stone-900">Siempre pasan al equipo</h2>
          <span className="text-xs text-stone-600">No se pueden cambiar</span>
        </div>
        <ul className="m-0 grid list-none grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-3 p-0">
          {ALWAYS_TEAM.map((item) => (
            <li key={item.title} className="rounded-[14px] border border-stone-200 bg-white p-3.5">
              <div className="text-sm font-semibold text-stone-900">{item.title}</div>
              <div className="mt-1 text-[13px] leading-[1.45] text-stone-600">{item.text}</div>
            </li>
          ))}
        </ul>
      </section>

      {toast && (
        <Toast
          message={toast.message}
          actionLabel={toast.undo ? 'Deshacer' : undefined}
          onAction={toast.undo}
          onDismiss={dismissToast}
        />
      )}
    </div>
  );
};
